// cloudSync.ts
// =============
//
// Wave 14ca-fix30: cross-device-sync für UX-Präferenzen.
// (Custom Trails laufen über customTrails.ts mit eigenem mirror-Helper.)
//
// Strategie:
//   - SYNCED_PREF_KEYS = Whitelist von localStorage-Keys die wir synchen.
//     Per-Device-Sachen (push-prompt, install-counter, auth-tokens) NICHT
//     in der Liste — die müssen pro Gerät bleiben.
//   - Beim Login / App-Start: einmal /api/me/prefs ziehen → server gewinnt
//     (server hat jüngsten Stand quer über alle Geräte).
//   - Beim lokalen Setzen eines whitelisted Keys: 2s debounce, dann den
//     gesamten whitelisted-Snapshot per PUT hochschicken.
//   - Wenn offline: PUT failt silent, lokaler Wert bleibt — beim nächsten
//     online-Event versuchen wir's wieder (siehe app.tsx online-effect).

const PREFIX = 'adt_';

/**
 * Cross-device synchronisierte Settings. NUR Sachen die UX-Präferenz sind
 * und Geräte-übergreifend Sinn ergeben.
 */
export const SYNCED_PREF_KEYS: readonly string[] = [
  'adt_screen_awake',
  'adt_icon_size',
  'adt_satellite_enabled',
  'adt_show_hillshade',
  'adt_show_crux',
  'adt_ride_zoom',
  'adt_explore_zoom',
  'adt_route_mode_road',
  'adt_poi_drinking_water',
  'adt_fallback_style',
  'adt_create_custom_tap_trails_seen',
];

const SYNCED_SET = new Set(SYNCED_PREF_KEYS);

/** Debounce-Timer für PUT-Calls (2s nach letztem set). */
let pushTimer: ReturnType<typeof setTimeout> | null = null;

/** Liest die aktuell gespeicherten whitelisted Werte aus localStorage. */
function snapshotPrefs(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of SYNCED_PREF_KEYS) {
    const v = localStorage.getItem(key);
    if (v !== null) out[key] = v;
  }
  return out;
}

/** Schreibt einen prefs-Snapshot vom Server in localStorage. Triggert
    Storage-Events damit usePreference()-Hooks neu rendern. */
function applyPrefsLocally(prefs: Record<string, unknown>) {
  for (const [key, val] of Object.entries(prefs)) {
    if (!SYNCED_SET.has(key)) continue; // defensiv — falls Server müll schickt
    if (typeof val !== 'string') continue;
    const old = localStorage.getItem(key);
    if (old === val) continue;
    localStorage.setItem(key, val);
    // Browser-Storage-Event feuert nur in ANDEREN Tabs. Damit unsere eigene
    // App neu rendert, dispatchen wir manuell ein synthetic StorageEvent.
    try {
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          oldValue: old,
          newValue: val,
          storageArea: localStorage,
        }),
      );
    } catch {
      // SSR / unsupported — stört nicht.
    }
  }
}

/** Vom Server holen + lokal applyen. Rufen wir beim App-Start + nach Login. */
export async function pullPrefs(): Promise<void> {
  try {
    const res = await fetch('/api/me/prefs', { credentials: 'include' });
    if (!res.ok) return; // 401 = nicht eingeloggt → silent
    const data = (await res.json()) as { prefs?: Record<string, unknown> };
    if (data && data.prefs) applyPrefsLocally(data.prefs);
  } catch {
    /* offline / network → ignore */
  }
}

/** Snapshot hochschicken (debounced). Wird von einem Storage-Listener
    aufgerufen sobald eine SYNCED-Key gesetzt wurde. */
function schedulePush(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(async () => {
    pushTimer = null;
    const prefs = snapshotPrefs();
    try {
      await fetch('/api/me/prefs', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefs }),
      });
    } catch {
      /* offline → next change will retry; nothing crashes */
    }
  }, 2000);
}

/**
 * Setup: an localStorage-Schreibungen lauschen (intercepting setItem) und
 * bei whitelisted Keys einen Push schedulen. Idempotent — beim zweiten
 * Aufruf passiert nichts mehr.
 */
let installed = false;
export function installPrefSyncListener(): void {
  if (installed) return;
  installed = true;
  // Wir patchen Storage.prototype.setItem damit wir alle Schreibungen
  // mitbekommen — auch die innerhalb derselben Tab passieren (Storage-
  // Events feuern nur cross-tab, nicht in derselben Tab).
  const orig = Storage.prototype.setItem;
  Storage.prototype.setItem = function patchedSetItem(key, value) {
    orig.call(this, key, value);
    if (this === localStorage && typeof key === 'string' && SYNCED_SET.has(key)) {
      schedulePush();
    }
  };
  // Auch removeItem — damit ein gelöschter Key nicht "sticky" bleibt nach Sync.
  const origRemove = Storage.prototype.removeItem;
  Storage.prototype.removeItem = function patchedRemoveItem(key) {
    origRemove.call(this, key);
    if (this === localStorage && typeof key === 'string' && SYNCED_SET.has(key)) {
      schedulePush();
    }
  };
}

// Suppress unused-export-check.
export const _PREFIX = PREFIX;
